/**
 * adsAi.ts — the contract between the CMS Worker and the AI Worker for writing ads (POST /v1/ads).
 * The AI Worker gets only what the model may use: the week's research, reviewer feedback and names to avoid.
 * It has no database access and saves nothing; drafts are saved by the CMS Worker after its own re-check.
 */
import { brandKitSchema } from "./adsBrand.ts";
import { z } from "zod";
import { aiModelChoiceSchema, aiProviderSchema } from "./ai.ts";
import { adChannelSchema, adProductIdSchema } from "./adsProducts.ts";
import { adConceptInputSchema, adConceptTagsSchema, adOriginForModelSchema, adPayloadSchema } from "./adsDrafts.ts";
import { angleSimilarity, AUTO_SELECT } from "./adsFeedback.ts";

export const AD_AI_LIMITS = { maxCount: 5, maxNoteChars: 1000, maxFeedbackChars: 3000 } as const;

export const adGenContextSchema = z.object({
  /** Competitor ads seen this week, for angles and gaps only. Never copied. */
  competitorAds: z.array(z.object({ company: z.string().max(120), headline: z.string().max(300), text: z.string().max(800) })).max(12).default([]),
  /** Research findings the ad may rely on, one line each. The only source of figures besides the fact sheet. */
  evidence: z.array(z.string().max(400)).max(20).default([]),
  /** "What reviewers told us in the last 8 weeks", already rendered. */
  feedback: z.string().max(AD_AI_LIMITS.maxFeedbackChars).default(""),
  competitorNames: z.array(z.string().max(120)).max(60).default([]),
  competitorHooks: z.array(z.string().max(300)).max(60).default([]),
  approvedNames: z.array(z.string().max(120)).max(20).default([]),
  blockedNames: z.array(z.string().max(120)).max(40).default([]),
  /** Raya only: how many concepts in this batch may be about small or solo practices. Null when the rule does not apply. */
  smallPracticeRoom: z.number().int().min(0).max(10).nullable().default(null),
  /** Up to five approved ads, newest first: examples of our tone and structure. Shown to the model as data, never to be copied. */
  referenceCopy: z.array(z.object({ text: z.string().max(1500), headline: z.string().max(300) })).max(5).default([]),
  /** A manager's additions to the brand kit: voice, phrases never to say, extra facts. */
  brandKit: brandKitSchema.default({ voice: "", neverSay: [], facts: [] }),
});
export type AdGenContext = z.infer<typeof adGenContextSchema>;

/** One idea from the first model, as the second model sees it. */
export const otherModelItemSchema = z.object({
  title: z.string().max(300),
  angle: z.string().max(300),
  evidence: z.string().max(2000),
  score: z.number().int().min(1).max(5).nullable(),
});
export type OtherModelItem = z.infer<typeof otherModelItemSchema>;

export const adGenerateRequestSchema = z.object({
  model: aiModelChoiceSchema,
  product: adProductIdSchema,
  channel: adChannelSchema,
  count: z.number().int().min(1).max(AD_AI_LIMITS.maxCount).default(3),
  /** Google only: the ad group and its core keyword the ads are written for. */
  target: z.object({ adGroup: z.string().trim().min(1).max(120), keyword: z.string().trim().min(1).max(120) }).optional(),
  context: adGenContextSchema.default({}),
  /** The first model's ideas, when this is the second pass. Data for the prompt, never instructions. */
  otherModel: z.object({ name: z.enum(["Grok", "Claude"]), items: z.array(otherModelItemSchema).max(AD_AI_LIMITS.maxCount) }).optional(),
  /** Where this batch starts, when the person began from an ad or a brief. */
  origin: adOriginForModelSchema.optional(),
  /** A reviewer asked for a rewrite: the draft as it stands and what to change. */
  regenerate: z.object({ previous: adPayloadSchema, note: z.string().trim().min(1).max(AD_AI_LIMITS.maxNoteChars) }).optional(),
});
export type AdGenerateRequest = z.infer<typeof adGenerateRequestSchema>;

export const adGenerateResponseSchema = z.object({
  items: z.array(z.object({ concept: adConceptInputSchema.extend({ tags: adConceptTagsSchema, writtenBy: z.string().min(1).max(100) }), payload: adPayloadSchema })).min(1),
  dropped: z.number().int().min(0),
  usage: z.object({ provider: aiProviderSchema, model: z.string(), inputTokens: z.number().int(), outputTokens: z.number().int() }),
});
export type AdGenerateResponse = z.infer<typeof adGenerateResponseSchema>;

// ---- Second-opinion review of one finished ad (POST /v1/ads/review) ----

export const AD_REVIEWERS = ["product_accuracy", "messaging"] as const;
export const adReviewerSchema = z.enum(AD_REVIEWERS);
export type AdReviewer = z.infer<typeof adReviewerSchema>;
export const AD_REVIEWER_LABEL: Record<AdReviewer, string> = { product_accuracy: "Product accuracy", messaging: "Messaging" };

export const adReviewRequestSchema = z.object({
  model: aiModelChoiceSchema,
  product: adProductIdSchema,
  channel: adChannelSchema,
  payload: adPayloadSchema,
  /** The product's fact sheet: the only things an ad may state about it. */
  facts: z.array(z.string().max(500)).max(60),
  /** The product's messaging rules, as sentences. */
  messagingRules: z.array(z.string().max(500)).max(40),
  /** Opening lines competitors use; context for the messaging reviewer. */
  competitorHooks: z.array(z.string().max(300)).max(60).default([]),
  /** Edited reviewer prompts (v92). Only the reviewer's own instruction; the security and output rules are always added by code. */
  prompts: z.object({ product_accuracy: z.string().max(4000).optional(), messaging: z.string().max(4000).optional() }).optional(),
});
export type AdReviewRequest = z.infer<typeof adReviewRequestSchema>;

export const adReviewFindingSchema = z.object({
  reviewer: adReviewerSchema,
  severity: z.enum(["advice", "flag"]),
  /** Which part of the ad it is about, e.g. "Post text" or "Headline 3". */
  field: z.string().trim().min(1).max(120),
  text: z.string().trim().min(1).max(400),
});
export type AdReviewFinding = z.infer<typeof adReviewFindingSchema>;

export const adReviewResponseSchema = z.object({
  findings: z.array(adReviewFindingSchema).max(20),
  usage: z.object({ provider: aiProviderSchema, model: z.string(), inputTokens: z.number().int(), outputTokens: z.number().int() }),
});
export type AdReviewResponse = z.infer<typeof adReviewResponseSchema>;

// ---- Two models, one list ----

/** The part of an idea that decides whether two models had the same one. */
export interface MarkableConcept {
  angle: string;
  evidence: string;
  suggestedBy: string | null;
}

/**
 * Computed here, never by a model. Each Claude idea is matched to the Grok idea it is most like (angle similarity at or
 * above the threshold auto-pick already uses for "reads like a rejected idea"; each Grok idea matches at most once).
 * A match marks BOTH as "Both", and Claude's evidence is added to Grok's, so the idea carries both lines of research.
 * Returns new arrays; the inputs are not changed.
 */
export function markBoth<T extends { concept: MarkableConcept }>(grokItems: readonly T[], claudeItems: readonly T[], threshold: number = AUTO_SELECT.maxSimilarityToRejected): { grok: T[]; claude: T[] } {
  const grok = grokItems.map((g) => ({ ...g, concept: { ...g.concept } }));
  const claude = claudeItems.map((c) => ({ ...c, concept: { ...c.concept } }));
  const taken = new Set<number>();
  for (const c of claude) {
    let best = -1;
    let bestScore = 0;
    grok.forEach((g, i) => {
      if (taken.has(i)) return;
      const s = angleSimilarity(c.concept.angle, g.concept.angle);
      if (s >= threshold && s > bestScore) {
        best = i;
        bestScore = s;
      }
    });
    if (best === -1) continue;
    taken.add(best);
    const g = grok[best]!;
    g.concept.suggestedBy = "Both";
    c.concept.suggestedBy = "Both";
    const extra = c.concept.evidence.trim() ? `Claude's research: ${c.concept.evidence.trim()}` : "";
    if (extra) {
      const room = Math.max(0, 2000 - extra.length - 1);
      g.concept.evidence = [g.concept.evidence.trim().slice(0, room), extra.slice(0, 2000)].filter(Boolean).join("\n");
    }
  }
  return { grok, claude };
}
